import { formatDateToApiUTC, formatToIso, hasItems } from "@repo/utils";
import useSWR from "swr";
import useSWRInfinite, { type SWRInfiniteConfiguration } from "swr/infinite";

import { type ApiError } from "../ApiError";
import * as fetcher from "../fetcher";
import { type Opportunity } from "../opportunity/types";
import type {
  Appointment,
  AppointmentsSearchFilters,
  AppointmentsSearchOptions,
  AppointmentsSearchResponse,
  CalendarAppointmentsSearchResponse,
} from "./types";

export function stringifyAppointmentSearchQuery(
  filters: AppointmentsSearchFilters,
  options: AppointmentsSearchOptions
): string {
  if (!filters || !options) {
    return "";
  }

  const query: Record<string, any> = {
    pageIndex: options?.pageIndex,
    pageSize: options?.pageSize,
    sortKey: options?.sortKey,
    sortOrder: options?.sortOrder,
  };

  if (filters.appointmentDate?.[0]) {
    query.appointmentDateFilter = formatToIso(filters.appointmentDate[0]);
  }

  if (filters.appointmentDate?.[1]) {
    query.appointmentDateFilter = `${formatToIso(
      filters.appointmentDate[0]
    )}-${formatToIso(filters.appointmentDate[1])}`;
  }

  if (filters.emailId) {
    query.coworkerFilter = filters.emailId.join(",");
  }

  if (filters.teamId) {
    query.teamsFilter = filters.teamId;
  }

  if (filters.assignment !== undefined) {
    query.assignment = filters.assignment.toString();
  }

  if (filters.buName !== undefined) {
    query.buFilter = filters.buName;
  }

  if (hasItems(filters.status)) {
    query.statusFilter = filters.status.join(",");
  }

  if (filters.assignedToTeam !== undefined) {
    query.assignedToTeam = filters.assignedToTeam;
  }

  if (filters.hasReasonCode !== undefined) {
    query.hasReasonCode = filters.hasReasonCode;
  }

  if (filters.searchTerm !== undefined) {
    query.searchTerm = filters.searchTerm;
  }

  const searchParams = new URLSearchParams(query);
  return searchParams.toString();
}

export function stringifyCalendarAppointmentsSearchQuery(
  filters: AppointmentsSearchFilters
): string {
  if (!filters) {
    return "";
  }

  const query: Record<string, any> = {};

  if (filters.appointmentDate?.[0]) {
    query.fromDate = formatDateToApiUTC(filters.appointmentDate[0]);
  }

  if (filters.appointmentDate?.[1]) {
    query.toDate = formatDateToApiUTC(filters.appointmentDate[1]);
  }

  if (filters.emailId) {
    query.coworkerFilter = filters.emailId.join(",");
  }

  if (filters.teamId) {
    query.teamsFilter = filters.teamId;
  }

  if (filters.openSlotUnit !== undefined) {
    query.openSlotUnit = filters.openSlotUnit;
  }

  if (hasItems(filters.status)) {
    query.statusFilter = filters.status.join(",");
  }

  return new URLSearchParams(query).toString();
}

export const useAppointmentsSearch = (
  filters: AppointmentsSearchFilters | undefined,
  options: AppointmentsSearchOptions,
  refresh?: boolean
) => {
  return useSWRInfinite<AppointmentsSearchResponse, ApiError>(
    (pageIndex) => {
      if (!filters) {
        return null;
      }
      const query = stringifyAppointmentSearchQuery(filters, {
        ...options,
        pageIndex,
      });

      return `/opportunities/appointments?${query}`;
    },
    (url: string) => fetcher.core.get(url),
    {
      revalidateOnFocus: refresh ?? true,
      refreshInterval: 60 * 1000,
    }
  );
};

export const useCalendarAppointmentsSearch = (
  filters: AppointmentsSearchFilters | undefined,
  refresh?: boolean,
  swrOptions?: Partial<SWRInfiniteConfiguration>
) => {
  return useSWRInfinite<CalendarAppointmentsSearchResponse, ApiError>(
    () => {
      if (!filters) {
        return null;
      }
      const query = stringifyCalendarAppointmentsSearchQuery(filters);

      return `/opportunities/appointments/calendar?${query}`;
    },
    (url: string) => fetcher.core.get(url),
    {
      // Refetch when the user returns to the tab, but at most once every
      // 5 minutes so alt-tabbing does not spam the BOKA-backed BE.
      revalidateOnFocus: refresh ?? true,
      focusThrottleInterval: 5 * 60 * 1000,

      // Retry safeguards: bound retries and back off so a transient error
      // does not amplify traffic while the backend is under pressure.
      errorRetryCount: 3,
      // Wait at least 60s between retries; SWR applies exponential backoff
      errorRetryInterval: 5 * 60 * 1000,
      ...swrOptions,
    }
  );
};

export const fetchAppointmentDetails = async (
  appointment: Pick<Appointment, "id" | "appointmentId" | "type">
): Promise<Appointment | null> => {
  const isBoka = appointment.type === "BOKA_APPOINTMENT";
  const idToUse = isBoka ? appointment.appointmentId : appointment.id;

  if (!idToUse) {
    return null;
  }

  const opportunityData: Opportunity = await fetcher.core.get(
    `/appointments/${idToUse}/opportunity`
  );

  return (
    opportunityData?.appointmentDetails?.find((detail) =>
      isBoka ? detail.appointmentId === idToUse : detail.id === idToUse
    ) || null
  );
};

export const useAppointmentFromOpportunity = (
  appointment?: Appointment,
  shouldFetch = true
) => {
  const isBoka = appointment?.type === "BOKA_APPOINTMENT";
  const idToUse = isBoka ? appointment?.appointmentId : appointment?.id;

  return useSWR(
    shouldFetch && idToUse ? `/appointments/${idToUse}/opportunity` : null,
    () => (appointment ? fetchAppointmentDetails(appointment) : null)
  );
};
